import type { ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

const badgeVariants = cva(
  [
    'inline-flex items-center gap-1.5',
    'rounded-full',
    'px-2.5 py-0.5',
    'text-[var(--text-xs)] font-medium',
    'border',
    'whitespace-nowrap',
  ],
  {
    variants: {
      tone: {
        neutral: [
          'bg-[var(--color-surface-elevated)] text-[var(--color-ink-muted)]',
          'border-[var(--color-border)]',
        ],
        success: ['bg-[var(--color-success-muted)] text-[var(--color-ink)]', 'border-transparent'],
        warn: ['bg-[var(--color-warn-muted)] text-[var(--color-ink)]', 'border-transparent'],
        danger: ['bg-[var(--color-danger-muted)] text-[var(--color-ink)]', 'border-transparent'],
        info: ['bg-[var(--color-info-muted)] text-[var(--color-ink)]', 'border-transparent'],
        accent: ['bg-[var(--color-accent-muted)] text-[var(--color-ink)]', 'border-transparent'],
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export interface BadgeProps extends VariantProps<typeof badgeVariants> {
  children: ReactNode;
  className?: string;
  'aria-label'?: string;
}

export function Badge({ tone, className, children, ...rest }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ tone }), className)} {...rest}>
      {children}
    </span>
  );
}
